import { Link } from 'react-router-dom';
import { EmptyState } from '../../components/ui/EmptyState';
import { formatCalendarDate } from '../../lib/format';
import type { Id, Project } from '../../api/types';
import type { MissedOccurrence } from './recurrence-api';

/**
 * 🔴🔴 THE MISSED VIEW. THIS IS THE FEATURE, NOT A REPORT BOLTED ONTO IT.
 *
 * `GET /recurrences/missed` is the endpoint db/002 was designed to make possible: one row per
 * EXPECTED period means a period nobody did is a QUERY rather than something a human has to notice.
 * The API's own header says the same thing, and adds the part that matters most — the `projectId`
 * filter is OPTIONAL because *"a per-project-only version puts the burden back on somebody
 * remembering to check each project, which is the failure the design replaced."*
 *
 * ⇒ This list is UNSCOPED by default. One screen answers "is any standing duty being dropped?".
 *
 * ⭐⭐ THE ORDER IS THE SERVER'S — `ORDER BY o.expected_on ASC`, i.e. the longest-standing failure
 * first. ⛔ It is not re-sorted here. That ordering is not cosmetic: a chronological-descending list
 * buries a fortnight-old miss under everything since, which is exactly how a 14-day gap went
 * unnoticed until a human found it.
 *
 * ⭐ `days_late` is the SERVER's number too, computed by Postgres against `current_date`. Recomputing
 * it in the browser would disagree with the server's own missed filter for several hours a day.
 */
export function MissedList({
  rows,
  projects,
  busyId,
  errors,
  onMaterialise,
}: {
  rows: readonly MissedOccurrence[];
  projects: readonly Project[];
  busyId: Id | null;
  errors: Record<string, string>;
  /** "Do this period late, deliberately" — `POST /occurrences/{id}/ticket`. */
  onMaterialise: (occurrence: MissedOccurrence) => void;
}) {
  if (rows.length === 0) {
    /**
     * ⭐ THE ALL-CLEAR, AND IT IS ONLY PRINTED ABOUT AN ANSWER WE ACTUALLY RECEIVED. The caller
     * renders this branch only when the request succeeded — a failed request never reaches here, so
     * "nothing missed" can never stand in for "we could not ask". That distinction is the entire
     * reason this feature exists, and it is enforced by where this component is mounted rather than
     * by a flag it could get wrong.
     */
    return (
      <EmptyState
        title="No missed periods"
        detail="Every expected period across every recurring duty has a ticket. This is an answer from the server, not an empty screen."
      />
    );
  }

  const worst = rows[0];

  return (
    <div>
      <div
        role="alert"
        className="mx-3 mb-3 rounded-md border-2 border-red-500 bg-red-50 px-3 py-3 text-red-900 sm:mx-4 dark:border-red-600 dark:bg-red-950/60 dark:text-red-100"
      >
        <p className="text-base font-bold">
          <span aria-hidden="true">⛔ </span>
          {rows.length} period{rows.length === 1 ? '' : 's'} missed
        </p>
        <p className="mt-0.5 text-sm">
          {worst ? (
            <>
              The oldest is <strong>{worst.days_late} days late</strong> — {worst.recurrence_title}.
              A duty cannot detect its own silence; that is why every expected period is a row.
            </>
          ) : null}
        </p>
      </div>

      <ul className="space-y-2 px-3 pb-4 sm:px-4">
        {rows.map((row) => (
          <li
            key={row.id}
            data-state="missed"
            aria-label={`${row.recurrence_title} — missed, expected ${row.expected_on}`}
            className="rounded border-l-8 border-red-500 bg-red-50 px-3 py-3 dark:border-red-600 dark:bg-red-950/50"
          >
            <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
              <span className="rounded bg-red-600 px-1.5 py-0.5 text-[10px] font-bold tracking-wide text-white uppercase">
                Missed
              </span>
              <span className="text-sm font-bold text-red-900 dark:text-red-100">
                {row.recurrence_title}
              </span>
              <span className="text-xs text-red-900/80 dark:text-red-100/80">
                {row.cadence}
                {/* ⚠️ The project NAME only if we have the list. A bare id is worse than nothing here,
                    and inventing a key from the id would be a guess. */}
                {projectLabel(projects, row.project_id)}
              </span>
            </div>

            <p className="mt-1 text-sm text-red-900 dark:text-red-100">
              Expected <strong>{formatCalendarDate(row.expected_on)}</strong> —{' '}
              <strong>{row.days_late} days late</strong>. No ticket was ever created for this period.
            </p>

            <div className="mt-2 flex flex-wrap items-center gap-2">
              {/*
                ⭐⭐ THE DELIBERATE-LATE ACTION. The generator does NOT back-fill, on purpose: a ticket
                manufactured for a period nobody worked would erase the evidence that it was skipped.
                A human choosing to do it late is a different event, and this button is where that
                choice is recorded. The occurrence keeps its `expected_on`, so the row becomes "done
                late" rather than "fine".
              */}
              <button
                type="button"
                disabled={busyId === row.id}
                onClick={() => onMaterialise(row)}
                className="h-8 rounded border border-red-600 bg-red-600 px-3 text-xs font-semibold text-white disabled:opacity-50"
              >
                {busyId === row.id ? 'Creating…' : 'Create the ticket now (late)'}
              </button>
              <Link
                to={`/backlog?project=${row.project_id}`}
                className="text-xs font-semibold text-red-900 underline dark:text-red-100"
              >
                Open the project
              </Link>
            </div>

            {errors[String(row.id)] ? (
              <p role="alert" className="mt-2 text-xs font-semibold text-red-900 dark:text-red-100">
                {errors[String(row.id)]}
              </p>
            ) : null}
          </li>
        ))}
      </ul>
    </div>
  );
}

function projectLabel(projects: readonly Project[], projectId: Id): string {
  const project = projects.find((entry) => String(entry.id) === String(projectId));
  return project ? ` · ${project.key}` : '';
}
